(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initComparison(root) {
    if (root.dataset.tetInitialized === 'true') return;

    const range = root.querySelector('[data-tet-comparison-range]');
    if (!range) return;

    const section = root.closest('[data-tet-image-comparison-section]');
    const details = section ? [...section.querySelectorAll('[data-tet-comparison-detail]')] : [];

    const updatePosition = () => {
      root.style.setProperty('--tet-comparison-position', `${range.value}%`);

      const comparisonSide = Number(range.value) < 50 ? 'after' : 'before';
      details.forEach((detail) => {
        const isActive = detail.dataset.tetComparisonDetail === comparisonSide;
        detail.hidden = !isActive;
        detail.classList.toggle('is-active', isActive);
      });
    };

    root.dataset.tetInitialized = 'true';
    if (reducedMotion) root.classList.add('tet-interactive--reduced-motion');
    range.addEventListener('input', updatePosition);
    updatePosition();
  }

  function initHotspots(root) {
    if (root.dataset.tetInitialized === 'true') return;

    const triggers = [...root.querySelectorAll('[data-tet-hotspot-trigger]')];
    const details = [...root.querySelectorAll('[data-tet-hotspot-detail]')];
    if (!triggers.length || !details.length) return;

    const selectHotspot = (id) => {
      const selectedDetail = details.find((detail) => detail.dataset.tetHotspotDetail === id);

      triggers.forEach((trigger) => {
        const isSelected = trigger.dataset.tetHotspotTrigger === id;
        trigger.classList.toggle('is-active', isSelected);
        trigger.setAttribute('aria-expanded', String(isSelected));
        if (trigger.getAttribute('role') === 'tab') {
          trigger.setAttribute('aria-selected', String(isSelected));
        }
      });

      details.forEach((detail) => {
        detail.hidden = detail.dataset.tetHotspotDetail !== id;
      });

      if (selectedDetail) {
        root.style.setProperty('--tet-detail-x', `${selectedDetail.dataset.tetDetailX}%`);
        root.style.setProperty('--tet-detail-y', `${selectedDetail.dataset.tetDetailY}%`);
      }
    };

    root.dataset.tetInitialized = 'true';
    if (reducedMotion) root.classList.add('tet-interactive--reduced-motion');
    triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => selectHotspot(trigger.dataset.tetHotspotTrigger));
    });
    root.addEventListener('shopify:block:select', (event) => {
      const selectedTrigger = triggers.find(
        (trigger) => trigger.dataset.tetHotspotBlockId === event.detail?.blockId,
      );
      if (selectedTrigger) selectHotspot(selectedTrigger.dataset.tetHotspotTrigger);
    });
    selectHotspot(triggers[0].dataset.tetHotspotTrigger);
  }

  function initHero(root) {
    if (root.dataset.tetInitialized === 'true') return;

    const slides = [...root.querySelectorAll('[data-tet-hero-slide]')];
    if (slides.length < 2) return;

    const status = root.querySelector('[data-tet-hero-status]');
    const previous = root.querySelector('[data-tet-hero-prev]');
    const next = root.querySelector('[data-tet-hero-next]');
    let activeIndex = 0;

    const setSlide = (index) => {
      activeIndex = (index + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === activeIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', String(!isActive));
      });
      if (status) {
        status.textContent = `${String(activeIndex + 1).padStart(2, '0')} / ${String(slides.length).padStart(2, '0')}`;
      }
    };

    const advance = () => setSlide(activeIndex + 1);
    previous?.addEventListener('click', () => setSlide(activeIndex - 1));
    next?.addEventListener('click', advance);
    root.dataset.tetInitialized = 'true';
    setSlide(0);

    if (!reducedMotion) {
      root.tetHeroTimer = window.setInterval(advance, 5500);
    }
  }

  function initProductRail(root) {
    if (root.dataset.tetInitialized === 'true') return;

    const viewport = root.querySelector('[data-tet-product-rail-viewport]');
    if (!viewport) return;

    const distance = () => Math.max(viewport.clientWidth * 0.86, 280);
    root.querySelector('[data-tet-rail-prev]')?.addEventListener('click', () => {
      viewport.scrollBy({ left: -distance(), behavior: reducedMotion ? 'auto' : 'smooth' });
    });
    root.querySelector('[data-tet-rail-next]')?.addEventListener('click', () => {
      viewport.scrollBy({ left: distance(), behavior: reducedMotion ? 'auto' : 'smooth' });
    });
    root.dataset.tetInitialized = 'true';
  }

  function initFeatureReel(root) {
    if (root.dataset.tetInitialized === 'true') return;

    const triggers = [...root.querySelectorAll('[data-tet-reel-trigger]')];
    const media = [...root.querySelectorAll('[data-tet-reel-media]')];
    const panels = [...root.querySelectorAll('[data-tet-reel-panel]')];
    const videos = [...root.querySelectorAll('[data-tet-reel-video] video')];
    const fullscreenButtons = [...root.querySelectorAll('[data-tet-reel-fullscreen]')];
    if (triggers.length < 2 || media.length !== triggers.length || panels.length !== triggers.length) return;

    const enterFullscreen = (video) => {
      if (typeof video.requestFullscreen === 'function') {
        const request = video.requestFullscreen();
        request?.catch(() => {});
        return;
      }

      if (typeof video.webkitEnterFullscreen === 'function') {
        try {
          video.webkitEnterFullscreen();
        } catch (error) {
          return;
        }
      }
    };

    const replayVideo = (video) => {
      const mediaItem = video.closest('[data-tet-reel-media]');
      if (!mediaItem?.classList.contains('is-active')) return;

      video.currentTime = 0;
      const playRequest = video.play();
      playRequest?.catch(() => {});
    };

    videos.forEach((video) => {
      video.autoplay = true;
      video.defaultMuted = true;
      video.loop = true;
      video.muted = true;
      video.playsInline = true;
      video.addEventListener('ended', () => replayVideo(video));
    });

    let activeIndex = 0;
    const selectFeature = (index) => {
      activeIndex = (index + triggers.length) % triggers.length;
      const activeId = String(activeIndex + 1);
      triggers.forEach((trigger) => {
        const active = trigger.dataset.tetReelTrigger === activeId;
        trigger.classList.toggle('is-active', active);
        trigger.setAttribute('aria-selected', String(active));
      });
      media.forEach((item) => {
        const active = item.dataset.tetReelMedia === activeId;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-hidden', String(!active));
      });
      panels.forEach((panel) => {
        panel.hidden = panel.dataset.tetReelPanel !== activeId;
      });

      videos.forEach((video) => {
        const isActive = video.closest('[data-tet-reel-media]')?.dataset.tetReelMedia === activeId;
        if (isActive) {
          if (video.readyState === 0) video.load();
          const playRequest = video.play();
          playRequest?.catch(() => {});
        } else {
          video.pause();
        }
      });
    };

    triggers.forEach((trigger, index) => trigger.addEventListener('click', () => selectFeature(index)));
    root.addEventListener('shopify:block:select', (event) => {
      const selectedTrigger = triggers.find(
        (trigger) => trigger.dataset.tetReelBlockId === event.detail?.blockId,
      );
      if (selectedTrigger) selectFeature(triggers.indexOf(selectedTrigger));
    });
    fullscreenButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const video = button.closest('[data-tet-reel-media]')?.querySelector('video');
        if (!video) return;

        const playRequest = video.play();
        playRequest?.catch(() => {});
        enterFullscreen(video);
      });
    });
    root.dataset.tetInitialized = 'true';
    selectFeature(0);

    if (!reducedMotion && root.dataset.tetReelAutoRotate === 'true') {
      root.tetFeatureReelTimer = window.setInterval(() => selectFeature(activeIndex + 1), 5000);
    }
  }

  function initReviews(root) {
    if (root.dataset.tetInitialized === 'true') return;

    const viewport = root.querySelector('[data-tet-review-viewport]');
    const cards = [...root.querySelectorAll('[data-tet-review-card]')];
    const dots = [...root.querySelectorAll('[data-tet-review-dot]')];
    if (!viewport || cards.length < 2) return;

    const scrollReviews = (direction) => {
      viewport.scrollTo({
        left: viewport.scrollLeft + direction * viewport.clientWidth,
        behavior: reducedMotion ? 'auto' : 'smooth',
      });
    };

    const setCardWidth = () => {
      const cardsPerPage = window.matchMedia('(max-width: 749px)').matches ? 1 : 2;
      root.style.setProperty('--tet-review-card-width', `${viewport.clientWidth / cardsPerPage}px`);
    };

    const updateDots = () => {
      if (!dots.length) return;
      const page = Math.round(viewport.scrollLeft / viewport.clientWidth);
      dots.forEach((dot, index) => {
        const active = index === page;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', String(active));
      });
    };

    root.querySelector('[data-tet-review-prev]')?.addEventListener('click', () => scrollReviews(-1));
    root.querySelector('[data-tet-review-next]')?.addEventListener('click', () => scrollReviews(1));
    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        viewport.scrollTo({
          left: Number(dot.dataset.tetReviewDot) * viewport.clientWidth,
          behavior: reducedMotion ? 'auto' : 'smooth',
        });
      });
    });
    viewport.addEventListener('scroll', updateDots, { passive: true });
    window.addEventListener('resize', setCardWidth, { passive: true });
    root.dataset.tetInitialized = 'true';
    setCardWidth();
    updateDots();
  }

  function init(scope = document) {
    scope.querySelectorAll('[data-tet-image-comparison]').forEach(initComparison);
    scope.querySelectorAll('[data-tet-hotspots]').forEach(initHotspots);
    scope.querySelectorAll('[data-tet-hero-carousel]').forEach(initHero);
    scope.querySelectorAll('[data-tet-product-rail]').forEach(initProductRail);
    scope.querySelectorAll('[data-tet-feature-reel]').forEach(initFeatureReel);
    scope.querySelectorAll('[data-tet-reviews]').forEach(initReviews);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
